import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, XCircle, AlertTriangle, Target } from 'lucide-react';
import './DoubleStandardsSlide.css';

// Import image
import doubleStandardsImg from '../images/images for slides/kritik_vs_propaganda folie8_doppelmoral.jpg';

function DoubleStandardsSlide({ showNotes }) {
  const legitimeCriticism = [
    { icon: '🇺🇦', text: 'Ukrainische Flüchtlinge wurden 2022 willkommen geheißen, syrische 2015 oft abgelehnt' },
    { icon: '🇪🇺', text: 'Die EU tolerierte jahrelang Demokratieabbau in Polen und Ungarn' },
    { icon: '🇸🇦', text: 'Verbündete wie Saudi-Arabien werden weniger kritisiert als Gegner' },
    { icon: '⚖️', text: 'Unterschiedliche Reaktionen auf ähnliche Menschenrechtsverletzungen' },
  ];

  const exploitation = [
    { icon: '🎯', text: 'Echte Widersprüche werden aufgegriffen und instrumentalisiert' },
    { icon: '💬', text: '"Warum dürfen Ukrainer Nationalisten sein, wir aber nicht?"' },
    { icon: '🎭', text: 'Ziel: Rassismus als legitime Position darstellen' },
    { icon: '💣', text: 'Langfristig: Vertrauen in demokratische Institutionen zerstören' },
  ];

  return (
    <div className="slide double-standards-slide">
      <div className="ds-layout">
        {/* Header */}
        <div className="ds-header">
          <motion.span
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="section-label"
          >
            Analyse
          </motion.span>
          
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="slide-title"
          >
            Doppelmoral
          </motion.h2>
          
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="slide-subtitle"
          >
            Echte Kritik vs. Propaganda – Ein kompliziertes Thema
          </motion.p>
        </div>

        <div className="ds-content">
          {/* Left Column - Legitimate Criticism */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
            className="criticism-column"
          >
            <div className="column-header valid">
              <CheckCircle size={20} />
              <h3>Legitime Kritik an westlicher Doppelmoral</h3>
            </div>
            
            <div className="points-container">
              {legitimeCriticism.map((item, index) => (
                <motion.div
                  key={index}
                  className="point-card valid"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 + index * 0.1 }}
                >
                  <span className="point-icon">{item.icon}</span>
                  <span className="point-text">{item.text}</span>
                </motion.div>
              ))}
            </div>

            {/* Image */}
            <div className="column-image">
              <img src={doubleStandardsImg} alt="Kritik vs Propaganda" className="column-photo" />
            </div>
          </motion.div>

          {/* Right Column - Exploitation */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
            className="exploitation-column"
          >
            <div className="column-header danger">
              <AlertTriangle size={20} />
              <h3>Wie die Neue Rechte das ausnutzt</h3>
            </div>
            
            <div className="points-container">
              {exploitation.map((item, index) => (
                <motion.div
                  key={index}
                  className="point-card danger"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 + index * 0.1 }}
                >
                  <span className="point-icon">{item.icon}</span>
                  <span className="point-text">{item.text}</span>
                </motion.div>
              ))}
            </div>

            {/* Analysis Box - moved here for better balance */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 }}
              className="analysis-box"
            >
              <div className="analysis-header">
                <Target size={18} />
                <h4>Die Analyse</h4>
              </div>
              <p>
                Kritik an Doppelmoral ist <strong>oft berechtigt</strong>. Aber die Neue Rechte 
                nutzt sie nicht, um <em>Gerechtigkeit</em> zu fordern, sondern um ihren eigenen 
                <strong className="danger"> Extremismus zu rechtfertigen</strong>.
              </p>
            </motion.div>

            {/* Whataboutism Box */}
            <div className="whataboutism-box">
              <div className="term-badge">Fachbegriff</div>
              <h4>Whataboutism</h4>
              <p>als politische Waffe</p>
            </div>
          </motion.div>
        </div>
      </div>

      {showNotes && (
        <div className="speaker-notes">
          <strong>📚 Zusatzinfos für Leser:</strong><br/>
          <strong>Die Analyse der Doppelmoral:</strong><br/>
          Westliche Staaten messen oft mit zweierlei Maß (z.B. Ukraine vs. Syrien Flüchtlinge, Sanktionen gegen Russland vs. Saudi-Arabien). 
          Das ist berechtigte Kritik.<br/><br/>
          <strong>Die Instrumentalisierung (Der "Twist"):</strong><br/>
          Rechtsextreme nutzen diese echten Widersprüche ("Whataboutism") nicht, um für mehr Gerechtigkeit zu sorgen, sondern um:<br/>
          1. Menschenrechte als "geopolitisches Tool" zu entlarven und damit generell zu entwerten.<br/>
          2. Den eigenen Extremismus zu rechtfertigen ("Wenn die das dürfen, dürfen wir das auch").<br/>
          Diese Heuchelei ist der beste "Dünger" für ihre Propaganda.
        </div>
      )}
    </div>
  );
}

export default DoubleStandardsSlide;
